import React, { useRef } from "react";
import {
  Layers,
  Play,
  Square,
  Trash2,
  Download,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Clock,
} from "lucide-react";

const STATUS_META = {
  pending: { label: "En espera", color: "#71717A", Icon: Clock },
  uploading: { label: "Subiendo", color: "#FFC82E", Icon: Loader2 },
  processing: { label: "Procesando", color: "#FFC82E", Icon: Loader2 },
  done: { label: "Listo", color: "#84CC16", Icon: CheckCircle2 },
  error: { label: "Error", color: "#E01F26", Icon: AlertTriangle },
};

export default function BatchPanel({
  files,
  running,
  canStart,
  onPickFiles,
  onStart,
  onStop,
  onClear,
  onRemoveItem,
  onDownloadItem,
  onDownloadAll,
}) {
  const inputRef = useRef(null);

  const completed = files.filter((f) => f.status === "done").length;
  const errored = files.filter((f) => f.status === "error").length;
  const downloadable = files.filter((f) => f.status === "done" && f.token).length;

  return (
    <section className="border-t border-[#27272A] pt-5" data-testid="batch-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[11px] uppercase tracking-[0.25em] text-[#A1A1AA] font-bold inline-flex items-center gap-1.5">
          <Layers size={12} strokeWidth={1.75} /> Modo lote
        </h3>
        <span className="font-mono-pro text-xs text-[#71717A]">
          {completed}/{files.length}
          {errored > 0 ? ` · ${errored} err` : ""}
        </span>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="video/*"
        multiple
        hidden
        onChange={(e) => {
          onPickFiles(Array.from(e.target.files || []));
          e.target.value = "";
        }}
        data-testid="batch-file-input"
      />

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={running}
        data-testid="batch-add-btn"
        className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-sm border border-[#27272A] bg-[#0A0A0A] hover:bg-[#1A1A1A] disabled:opacity-40 text-xs uppercase tracking-[0.18em] text-[#FDFDFD] transition-colors"
      >
        <Layers size={13} strokeWidth={1.75} />
        Añadir varios archivos
      </button>

      {files.length > 0 && (
        <>
          <div
            className="flex flex-col gap-1.5 mt-3 max-h-56 overflow-y-auto pr-1"
            data-testid="batch-list"
          >
            {files.map((it) => {
              const meta = STATUS_META[it.status] || STATUS_META.pending;
              const Icon = meta.Icon;
              const animate =
                it.status === "uploading" || it.status === "processing";
              return (
                <div
                  key={it.id}
                  className="flex items-center gap-2 bg-[#0A0A0A] border border-[#27272A] rounded-sm px-2 py-2"
                  data-testid={`batch-item-${it.id}`}
                >
                  <Icon
                    size={13}
                    strokeWidth={1.75}
                    style={{ color: meta.color }}
                    className={animate ? "animate-spin" : ""}
                  />
                  <div className="flex-1 min-w-0">
                    <div
                      className="text-xs truncate text-[#FDFDFD]"
                      title={it.file.name}
                    >
                      {it.file.name}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex-1 h-1 bg-[#27272A] rounded overflow-hidden">
                        <div
                          className="h-full transition-all"
                          style={{
                            width: `${it.progress || 0}%`,
                            background:
                              it.status === "error"
                                ? "#E01F26"
                                : it.status === "done"
                                  ? "#84CC16"
                                  : "#FFC82E",
                          }}
                        />
                      </div>
                      <span
                        className="font-mono-pro text-[10px]"
                        style={{ color: meta.color }}
                      >
                        {it.status === "done"
                          ? `${it.elapsed_sec ?? "—"}s`
                          : it.status === "error"
                            ? "err"
                            : `${it.progress || 0}%`}
                      </span>
                    </div>
                    {it.error && (
                      <div
                        className="text-[10px] text-[#E01F26] mt-1 truncate"
                        title={it.error}
                      >
                        {it.error}
                      </div>
                    )}
                  </div>
                  {it.status === "done" && it.token && (
                    <button
                      type="button"
                      onClick={() => onDownloadItem(it)}
                      data-testid={`batch-download-${it.id}`}
                      title="Descargar"
                      className="p-1.5 text-[#FFC82E] hover:bg-[#FFC82E]/10 rounded transition-colors"
                    >
                      <Download size={13} strokeWidth={1.75} />
                    </button>
                  )}
                  {it.status === "pending" && !running && (
                    <button
                      type="button"
                      onClick={() => onRemoveItem(it.id)}
                      data-testid={`batch-remove-${it.id}`}
                      title="Quitar"
                      className="p-1.5 text-[#71717A] hover:text-[#E01F26] transition-colors"
                    >
                      <Trash2 size={12} strokeWidth={1.75} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-2 gap-2 mt-3">
            {!running ? (
              <button
                type="button"
                onClick={onStart}
                disabled={!canStart}
                data-testid="batch-start-btn"
                className="inline-flex items-center justify-center gap-1.5 py-2 rounded-sm bg-[#E01F26] hover:bg-[#B3181E] disabled:opacity-40 disabled:hover:bg-[#E01F26] text-white text-[11px] uppercase tracking-[0.18em] font-bold transition-colors"
              >
                <Play size={12} strokeWidth={2} /> Iniciar lote
              </button>
            ) : (
              <button
                type="button"
                onClick={onStop}
                data-testid="batch-stop-btn"
                className="inline-flex items-center justify-center gap-1.5 py-2 rounded-sm border border-[#E01F26] text-[#E01F26] hover:bg-[#E01F26]/10 text-[11px] uppercase tracking-[0.18em] font-bold transition-colors"
              >
                <Square size={12} strokeWidth={2} /> Detener
              </button>
            )}

            {downloadable > 0 ? (
              <button
                type="button"
                onClick={onDownloadAll}
                data-testid="batch-download-all-btn"
                className="inline-flex items-center justify-center gap-1.5 py-2 rounded-sm bg-[#FFC82E] hover:bg-[#D9AA26] text-[#0A0A0A] text-[11px] uppercase tracking-[0.18em] font-bold transition-colors"
              >
                <Download size={12} strokeWidth={2} /> Descargar todos ({downloadable})
              </button>
            ) : (
              <button
                type="button"
                onClick={onClear}
                disabled={running}
                data-testid="batch-clear-btn"
                className="inline-flex items-center justify-center gap-1.5 py-2 rounded-sm border border-[#27272A] text-[#A1A1AA] hover:bg-[#1A1A1A] disabled:opacity-40 text-[11px] uppercase tracking-[0.18em] transition-colors"
              >
                <Trash2 size={12} strokeWidth={1.75} /> Limpiar
              </button>
            )}
          </div>
          <p className="text-[10px] text-[#71717A] mt-2 leading-relaxed">
            Usa la ROI y feather actuales para todos los archivos. Procesa
            secuencialmente · descarga borra del servidor.
          </p>
        </>
      )}
    </section>
  );
}
